import { saveSeeAllMAContent, type MaUserConfiguration } from '@/lib/ma-user-config-api';
import { zyAuthor, zyServer } from '@/lib/servers';

const UserRoles: Record<string, string> = {
    super: 'super',
};

const TIPTAP_TOKEN_REFRESH_BUFFER_SECONDS = 60;

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
        return null;
    }
}

export function isTiptapTokenValid(token: string | null, bufferSeconds = TIPTAP_TOKEN_REFRESH_BUFFER_SECONDS): boolean {
    if (!token) {
        return false;
    }

    const payload = decodeJwtPayload(token);
    const exp = payload?.exp;
    if (typeof exp !== 'number') {
        return false;
    }

    return exp > Math.floor(Date.now() / 1000) + bufferSeconds;
}

export interface ZybookUser {
    // Define user properties as needed
    [key: string]: any;
}

interface SessionData {
    authToken: string;
    tiptapToken: string;
    refreshToken: string;
    timestamp: number;
}

export class AuthService {
    private authToken: string | null;
    private refreshToken: string | null;
    private expiryDate: Date | null;
    private userId: string | null;
    private user: ZybookUser | null;
    private tiptapToken: string | null;
    private refreshTimer?: number;
    private refreshPromise: Promise<boolean> | null = null;
    private tiptapTokenRefreshPromise: Promise<string | null> | null = null;

    constructor() {
        this.authToken = null;
        this.refreshToken = null;
        this.expiryDate = null;
        this.userId = null;
        this.tiptapToken = null;
        this.user = null;
        this.loadSession();
    }

    async signIn(email: string, password: string): Promise<{ success: boolean; user: ZybookUser | null }> {
        // Get auth token
        const signInData = await zyServer.post('signin', {
            headers: { 'Content-Type': 'application/json' },
            body: { email, password },
            skipAuthRetry: true,
        });
        this.authToken = signInData.session.auth_token;
        this.refreshToken = signInData.session.refresh_token;
        this.expiryDate = new Date(signInData.session.expiry_date);
        this.userId = String(signInData.session.user_id);
        this.user = signInData.user;

        this.tiptapToken = await this.fetchTiptapToken();

        await this.loadMaUserConfiguration();

        this.persistSession();
        this.scheduleTokenRefresh();

        return {
            success: true,
            user: this.user,
        };
    }

    hasMaPermissionsCache(): boolean {
        return this.user?.maPermissions !== undefined || this.user?.maPermissions !== null;
    }

    async loadMaUserConfiguration(): Promise<void> {
        if (!this.isAuthenticated()) {
            return;
        }

        try {
            const data = await zyAuthor.get<MaUserConfiguration>('modern_authoring/user_configuration');
            if (this.user) {
                this.user.maPermissions = data.permissions ?? {};
            }
        } catch (error) {
            console.error('Failed to load MA user configuration:', error);
        }
    }

    async hydrateMaPermissionsIfMissing(): Promise<void> {
        if (!this.isAuthenticated() || this.hasMaPermissionsCache()) {
            return;
        }

        await this.loadMaUserConfiguration();
        this.persistSession();
    }

    /**
     * Persists the current user session data to localStorage.
     *
     * @remarks
     * This approach is not ideal from a security perspective.
     * It is a tradeoff made for usability.
     */
    persistSession(): void {
        localStorage.setItem('user', JSON.stringify(this.user));
        if (this.userId) localStorage.setItem('userId', this.userId);
        if (this.expiryDate) localStorage.setItem('expiryDate', this.expiryDate.toISOString());

        const sessionData: SessionData = {
            authToken: this.authToken!,
            tiptapToken: this.tiptapToken!,
            refreshToken: this.refreshToken!,
            timestamp: Date.now(),
        };
        localStorage.setItem('session', btoa(JSON.stringify(sessionData)));
    }

    loadSession(): boolean {
        console.log('Loading session from localStorage');
        try {
            const user = localStorage.getItem('user');
            const expiryDate = localStorage.getItem('expiryDate');
            const sessionData = localStorage.getItem('session');

            if (user && expiryDate && sessionData) {
                this.user = JSON.parse(user);
                this.expiryDate = new Date(expiryDate);

                if (this.expiryDate > new Date()) {
                    const decoded: SessionData = JSON.parse(atob(sessionData));
                    this.authToken = decoded.authToken;
                    this.refreshToken = decoded.refreshToken;
                    this.userId = localStorage.getItem('userId');
                    this.tiptapToken = decoded.tiptapToken;

                    this.scheduleTokenRefresh();

                    return true;
                }
            }
        } catch (error) {
            console.error('Failed to load session:', error);
        }

        this.clearSession();
        return false;
    }

    scheduleTokenRefresh(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        if (!this.expiryDate) return;

        const now = new Date().getTime();
        const expiry = this.expiryDate.getTime();
        const timeUntilExpiry = expiry - now;

        const refreshTime = timeUntilExpiry - 5 * 60 * 1000;

        if (refreshTime > 0) {
            this.refreshTimer = window.setTimeout(() => {
                this.refreshAuthToken();
            }, refreshTime);
        } else {
            this.refreshAuthToken();
        }
    }

    async refreshAuthToken(): Promise<boolean> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.doRefreshAuthToken().finally(() => {
            this.refreshPromise = null;
        });

        return this.refreshPromise;
    }

    private async doRefreshAuthToken(): Promise<boolean> {
        try {
            const refreshData = await zyServer.get('refresh', {
                params: { refresh_token: this.refreshToken },
                skipAuthRetry: true,
            });

            this.authToken = refreshData.session.auth_token;
            this.refreshToken = refreshData.session.refresh_token;
            this.expiryDate = new Date(refreshData.session.expiry_date);

            this.tiptapToken = await this.fetchTiptapToken();

            this.persistSession();
            this.scheduleTokenRefresh();

            return true;
        } catch (error) {
            console.error('Token refresh error:', error);
            // The session ran out rather than the user signing out, so send them back here after signing in.
            this.signOut({ returnToCurrentPage: true });
            return false;
        }
    }

    private async fetchTiptapToken(): Promise<string> {
        const tiptapData = await zyServer.post('modern_authoring/generate_tiptap_token', { body: {} });
        return tiptapData.session.tiptap_token;
    }

    private async doRefreshTiptapToken(): Promise<string | null> {
        if (!this.isAuthenticated()) {
            return null;
        }

        try {
            this.tiptapToken = await this.fetchTiptapToken();
            this.persistSession();
            return this.tiptapToken;
        } catch (error) {
            console.error('TipTap token refresh error:', error);
            return null;
        }
    }

    getAuthHeaders(): Record<string, string> {
        if (!this.isAuthenticated()) {
            throw new Error('Not authenticated');
        }
        return {
            Authorization: `Bearer ${this.authToken}`,
        };
    }

    getAuthToken(): string | null {
        return this.authToken;
    }

    getRefreshToken(): string | null {
        return this.refreshToken;
    }

    isAuthenticated(): boolean {
        return !!(this.authToken && this.expiryDate && this.expiryDate > new Date());
    }

    /**
     * Clears the session and opens the login page. With `returnToCurrentPage`, signing in again goes
     * back to the page the user was on, as the router does for a visit without a session.
     */
    signOut({ returnToCurrentPage = false }: { returnToCurrentPage?: boolean } = {}): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.clearSession();
        const { pathname, search } = window.location;
        // Already on the login page, for example sent here with a redirectTo while a failed token refresh
        // was still in flight: stay, so that redirectTo is not dropped.
        if (pathname === '/login') {
            return;
        }
        const returnTo = returnToCurrentPage ? `?redirectTo=${encodeURIComponent(pathname + search)}` : '';
        window.location.href = `/login${returnTo}`;
    }

    clearSession(): void {
        this.authToken = null;
        this.refreshToken = null;
        this.expiryDate = null;
        this.tiptapToken = null;
        this.userId = null;
        this.user = null;
        localStorage.clear();
    }

    getUser(): ZybookUser | null {
        return this.user;
    }

    getUserId(): string | null {
        return this.userId;
    }

    getUserFullName(): string | null {
        return this.user?.first_name + ' ' + this.user?.last_name;
    }

    getUserPrimaryEmail(): string | null {
        const userEmails = this.user?.emails;
        if (!userEmails) return null;
        return userEmails.find((email: any) => email.is_primary)?.address || null;
    }

    getUserPasswordLastUpdated(): string | null {
        const passwordLastUpdated = this.user?.metadata?.password_last_updated;
        if (!passwordLastUpdated) return null;
        return new Date(passwordLastUpdated).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    async getTiptapToken(force = false): Promise<string | null> {
        if (!this.isAuthenticated()) {
            return null;
        }

        if (!force && isTiptapTokenValid(this.tiptapToken)) {
            return this.tiptapToken;
        }

        if (this.tiptapTokenRefreshPromise) {
            return this.tiptapTokenRefreshPromise;
        }

        this.tiptapTokenRefreshPromise = this.doRefreshTiptapToken().finally(() => {
            this.tiptapTokenRefreshPromise = null;
        });

        return this.tiptapTokenRefreshPromise;
    }

    isSuper(): boolean {
        return this.user?.role === UserRoles.super;
    }

    canSeeAllMAContent(): boolean {
        return Boolean(this.user?.maPermissions?.seeAllMAContent) || false;
    }

    canToggleAllMAContent(): boolean {
        return Boolean(this.user?.maPermissions?.toggleAllMAContent) || false;
    }

    /** MA Admin routes and orphan-cleanup tools require MaUserConfiguration isDeveloperAdmin. */
    canAccessAdmin(): boolean {
        return Boolean(this.user?.maPermissions?.isDeveloperAdmin) || false;
    }

    async toggleSeeAllMAContent(): Promise<boolean> {
        if (!this.canToggleAllMAContent()) return false;

        try {
            const permissions = await saveSeeAllMAContent(!this.canSeeAllMAContent());
            if (this.user) this.user.maPermissions = permissions;
            this.persistSession();
            return true;
        } catch (error) {
            console.error('Failed to toggle see-all MA content', error);
            return false;
        }
    }

    getUserInitials(): string {
        const fullName = this.getUserFullName();
        if (!fullName) return '?';
        return fullName
            .split(' ')
            .map(name => name[0].toUpperCase())
            .join('')
            .slice(0, 2);
    }

    getUserColor(): string {
        const userId = this.getUserId();
        if (!userId) return '#FFFFFF';

        let hash = 0;
        const str = String(userId);
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
            hash |= 0;
        }

        const hue = Math.abs(hash) % 360;
        let saturation = 55 + (Math.abs(hash >> 8) % 20);
        let lightness = 75 + (Math.abs(hash >> 16) % 15);

        saturation /= 100;
        lightness /= 100;
        const a = saturation * Math.min(lightness, 1 - lightness);
        const f = (n: number) => {
            const k = (n + hue / 30) % 12;
            const color = lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
            return Math.round(255 * color)
                .toString(16)
                .padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    }

    getCollaborationUser(): { id: string; name: string; color: string; initials: string } {
        const fullName = authService.getUserFullName();
        const color = authService.getUserColor();
        const initials = authService.getUserInitials();

        return {
            id: this.getUserId() ?? '',
            name: fullName ?? 'Anonymous Author',
            color: color ?? '#FFFFFF',
            initials: initials ?? 'AA',
        };
    }
}

const authService = new AuthService();
export default authService;
